import { useCallback, useRef, useState, useEffect, Fragment } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { Chip, Grid } from '@mui/material';
import { FormModal } from './FormModal';
import { DeleteConfirmation } from './DeleteConfirmation';
import {
  AutocompleteInput,
  // AutocompleteInput,
  CheckboxInput,
  SelectInput,
  TextInput,
} from '../inputs';
import {
  useDispatch,
  addResource,
  deleteResource,
  fetchResourceById,
  useSelector,
  systemSelectors,
} from '../../../state';
import { authGet, convertNullFieldsToEmptyString } from '../../../lib';
import {
  SelectOption,
  WarehouseLocationAPI,
  WarehouseLocationType,
  WarehouseStagingAreaType,
  WarehouseStorageCategoryType,
  WarehouseVehicleType,
} from '../../../types';

interface Props {
  open: boolean;
  handleClose: () => any;
  warehouseId: string | number;
  rowId?: number | string;
  aisleId: number | string;
  position?: number | string;
  locationId?: number | string;
  locationType: WarehouseLocationType;
  aisleNumber?: string | number;
  setViewVersion?: React.Dispatch<React.SetStateAction<number>>;
}

interface State
  extends Omit<
    WarehouseLocationAPI,
    | 'warehouseID'
    | 'accessibilityType'
    | 'category'
    | 'stagingCategory'
    | 'type'
  > {
  // freeTextPosition: string;
  category: WarehouseStorageCategoryType | '';
  stagingCategory: WarehouseStagingAreaType | '';
  type: WarehouseLocationType | '';
}

const initialState: State = {
  type: '',
  position: '',
  // freeTextPosition: '',
  level: '',
  externalID: '',
  category: '',
  stagingCategory: '',
  capableWeightPounds: '',
  actualLengthInches: '',
  actualWidthInches: '',
  actualHeightInches: '',
  capableLengthInches: '',
  capableWidthInches: '',
  capableHeightInches: '',
  isEasyToAccess: false,
  vehicleTypes: [],
};

export function WarehouseLocationForm({
  open,
  handleClose,
  warehouseId,
  aisleId,
  aisleNumber,
  rowId,
  position,
  locationId,
  locationType,
  setViewVersion,
}: Props) {
  const { classes, css } = useStyles();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(initialState);
  const [billingWarningOpen, setBillingWarningOpen] = useState(false);
  const billingWarningConfirmedRef = useRef(false);
  const [vehicleTypes, setVehicleTypes] = useState<
    Array<SelectOption<WarehouseVehicleType>>
  >([]);
  const [vehiclesObj, setVehiclesObj] = useState<Record<string, string>>({});
  // const [positionOptions, setPositionOptions] = useState<Array<IdNamePair>>([]);

  const locationCategories = useSelector(
    systemSelectors.warehouseStorageCategoryTypes,
  );

  const stagingCategories = useSelector(
    systemSelectors.warehouseStagingAreaTypes,
  );

  const locationTypes = useSelector(systemSelectors.warehouseLocationTypes);

  const _vehicleTypes = useSelector(systemSelectors.warehouseVehicleTypes);

  useEffect(() => {
    if (_vehicleTypes) {
      const filtered = _vehicleTypes.filter((v) => v.id !== 'Other');
      setVehicleTypes(filtered);
      const obj = filtered.reduce(
        (acc, cur) => {
          acc[cur.id] = cur.name as string;
          return acc;
        },
        {} as Record<string, string>,
      );
      setVehiclesObj(obj);
    }
  }, [_vehicleTypes]);

  useEffect(() => {
    (async function () {
      if (aisleId && position && locationId) {
        const { data } = await dispatch(
          fetchResourceById<WarehouseLocationAPI>({
            baseUrl: '/warehouses',
            id: warehouseId,
            path: `aisles/${aisleId}/rows/${rowId}/positions/${position}/locations/${locationId}`,
          }),
        );
        if (data) {
          const { accessibilityType, ...rest } = data;
          setState((cur) => ({
            ...cur,
            ...convertNullFieldsToEmptyString(rest),
            isEasyToAccess: accessibilityType === 'Easy',
          }));
        }
      } else {
        setState((cur) => ({
          ...cur,
          type: locationType,
          position: position || '',
        }));
      }
    })();
  }, [
    warehouseId,
    dispatch,
    aisleId,
    position,
    locationId,
    locationType,
    rowId,
  ]);

  // useEffect(() => {
  //   (async function () {
  //     if (!position) {
  //       const { data } = await authGet<Array<IdNamePair>>(
  //         `/warehouses/${warehouseId}/aisles/${aisleId}/positions/basic`,
  //       );
  //       if (data) {
  //         setPositionOptions(data);
  //       }
  //     }
  //   })();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const handleSubmit = useCallback(
    async (values: State) => {
      const {
        isEasyToAccess,
        category,
        stagingCategory,
        type,
        // position,
        // freeTextPosition,
        ...rest
      } = values;

      if (!type) {
        return;
      }

      if (
        !billingWarningConfirmedRef.current &&
        type === 'LongTermStorage' &&
        category &&
        values.actualLengthInches &&
        values.actualWidthInches &&
        values.actualHeightInches
      ) {
        setSubmitting(true);
        const { data: hasMatch, error } = await authGet<boolean>([
          `/warehouses/${warehouseId}/fees/has-matching-storage-fee`,
          {
            category,
            lengthInches: values.actualLengthInches,
            widthInches: values.actualWidthInches,
            heightInches: values.actualHeightInches,
          },
        ]);
        setSubmitting(false);
        if (!error && hasMatch === false) {
          setBillingWarningOpen(true);
          return;
        }
      }

      setSubmitting(true);
      const payload: WarehouseLocationAPI = {
        ...rest,
        id: values.id || null,
        warehouseID: warehouseId,
        type,
        // position: position || freeTextPosition,
        accessibilityType: isEasyToAccess ? 'Easy' : 'Hard',
        category: category || undefined,
        stagingCategory: stagingCategory || undefined,
      };
      const { error } = await dispatch(
        addResource<WarehouseLocationAPI>({
          baseUrl: `/warehouses/${warehouseId}/aisles/${aisleId}/rows/${rowId}/locations`,
          payload,
          message: 'Changes saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        setViewVersion && setViewVersion((cur) => ++cur);
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [warehouseId, aisleId, rowId],
  );

  const handleDelete = useCallback(async () => {
    setSubmitting(true);
    const response = await dispatch(
      deleteResource({
        baseUrl: '/warehouses',
        id: warehouseId,
        path: `aisles/${aisleId}/rows/${rowId}/positions/${position}/locations/${locationId}`,
      }),
    );
    setSubmitting(false);
    const { error } = response;
    if (!error) {
      setViewVersion && setViewVersion((cur) => ++cur);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, aisleId, rowId, position, locationId]);

  const formik = useFormik({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  // const { handleChange, ...formikProps } = formik;
  // const { setFieldValue } = formik;

  // const customOnChange = async (e: InputChangeEventType) => {
  //   const { name } = e.target;

  //   if (name === 'position') {
  //     setFieldValue('freeTextPosition', '');
  //   } else if (name === 'freeTextPosition') {
  //     setFieldValue('position', '');
  //   }

  //   handleChange(e);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // };

  return (
    <Fragment>
      <FormModal
        open={open}
        handleClose={handleClose}
        title={
          <span className={classes.title}>{`Warehouse location ${aisleNumber}${
            !!position ? '-' + position : ''
          }`}</span>
        }
        callback={formik.handleSubmit}
        btnText='SAVE'
        typeSubmit
        maxWidth='lg'
        paperProps={{ style: { width: 720 } }}
        submitting={submitting}
        isEdit={!!locationId}
        handleDelete={handleDelete}
      >
        <div className={classes.layout}>
          {!position && (
            <Fragment>
              <div
                className={classes.subSectionHeader}
                style={{ marginTop: 0 }}
              >
                Aisle position info
              </div>
              <Grid container spacing={5} rowSpacing={2}>
                <Grid
                  style={{ paddingBottom: 0 }}
                  size={{
                    sm: 12,
                  }}
                >
                  <TextInput
                    name='position'
                    formikProps={formik}
                    label='Enter new position'
                    className={classes.input}
                    type='number'
                    required
                  />
                </Grid>
              </Grid>
            </Fragment>
          )}
          {/* {!position && (
          <Fragment>
            <div className={classes.subSectionHeader} style={{ marginTop: 0 }}>
              Aisle position info
            </div>
            <Grid container spacing={5} rowSpacing={2}>
              <Grid sm={6} style={{ paddingBottom: 0 }}>
                <AutocompleteInput
                  formikProps={formikProps}
                  textFieldProps={{
                    name: 'position',
                    label: 'Select position',
                    onChange: customOnChange,
                    required: !formik.values.freeTextPosition,
                  }}
                  autocompleteProps={{
                    className: classes.input,
                    options: positionOptions,
                  }}
                />
              </Grid>
              <Grid sm={6} style={{ paddingBottom: 0 }}>
                <TextInput
                  name='freeTextPosition'
                  formikProps={formikProps}
                  handleChange={customOnChange}
                  useCustomValue
                  label='Enter new position'
                  className={classes.input}
                  type='number'
                  required={!formik.values.position}
                />
              </Grid>
            </Grid>
          </Fragment>
        )} */}
          <div
            className={classes.subSectionHeader}
            style={{ marginTop: !!position ? 0 : undefined }}
          >
            Location info
          </div>
          <Grid container spacing={5} rowSpacing={2}>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='level'
                formikProps={formik}
                label='Level'
                className={classes.input}
                required={locationType === 'LongTermStorage'}
              />
            </Grid>
            {!!locationId && (
              <Grid
                style={{ paddingBottom: 0 }}
                size={{
                  sm: 6,
                }}
              >
                <SelectInput
                  name='type'
                  formikProps={formik}
                  label='Location type'
                  className={classes.input}
                  options={locationTypes}
                  required
                />
              </Grid>
            )}
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <SelectInput
                name='category'
                formikProps={formik}
                label='Storage category'
                className={classes.input}
                options={locationCategories}
                required={locationType === 'LongTermStorage'}
              />
            </Grid>
            {locationType === 'Staging' && (
              <Grid
                style={{ paddingBottom: 0 }}
                size={{
                  sm: 6,
                }}
              >
                <SelectInput
                  name='stagingCategory'
                  formikProps={formik}
                  label='Staging category'
                  className={classes.input}
                  options={stagingCategories}
                />
              </Grid>
            )}
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='externalID'
                formikProps={formik}
                label='Reference #'
                className={classes.input}
              />
            </Grid>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='capableWeightPounds'
                formikProps={formik}
                label='Weight capabilities'
                className={classes.input}
                type='number'
                required={
                  locationType === 'LongTermStorage' &&
                  formik.values.category !== 'Floor'
                }
              />
            </Grid>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 12,
              }}
            >
              <AutocompleteInput
                formikProps={formik}
                textFieldProps={{
                  name: 'vehicleTypes',
                  label: 'Which vehicles are needed to access location?',
                }}
                autocompleteProps={{
                  // className: classes.smallInput,
                  options: vehicleTypes,
                  multiple: true,
                  renderTags: (tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                      const { key, ...otherProps } = getTagProps({ index });
                      const optionId =
                        typeof option === 'object' && option !== null
                          ? option.id
                          : option;
                      return (
                        <Chip
                          key={key}
                          label={vehiclesObj[optionId]}
                          {...otherProps}
                        />
                      );
                    }),
                }}
              />
            </Grid>
          </Grid>
          <div className={classes.subSectionHeader}>Actual dimensions</div>
          <Grid container spacing={5} rowSpacing={2}>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='actualLengthInches'
                formikProps={formik}
                label='Length'
                className={classes.input}
              />
            </Grid>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='actualWidthInches'
                formikProps={formik}
                label='Width'
                className={classes.input}
              />
            </Grid>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='actualHeightInches'
                formikProps={formik}
                label='Height'
                className={classes.input}
              />
            </Grid>
          </Grid>
          <div className={classes.subSectionHeader}>Capable Dimensions</div>
          <Grid container spacing={5} rowSpacing={2}>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='capableLengthInches'
                formikProps={formik}
                label='Length'
                className={classes.input}
                required={
                  locationType === 'LongTermStorage' &&
                  formik.values.category !== 'Floor'
                }
              />
            </Grid>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='capableWidthInches'
                formikProps={formik}
                label='Width'
                className={classes.input}
                required={
                  locationType === 'LongTermStorage' &&
                  formik.values.category !== 'Floor'
                }
              />
            </Grid>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 6,
              }}
            >
              <TextInput
                name='capableHeightInches'
                formikProps={formik}
                label='Height'
                className={classes.input}
                required={
                  locationType === 'LongTermStorage' &&
                  formik.values.category !== 'Floor'
                }
              />
            </Grid>
            <Grid
              style={{ paddingBottom: 0 }}
              size={{
                sm: 12,
              }}
            >
              <div className={css({ marginTop: 24 })}>
                <CheckboxInput
                  formikProps={formik}
                  checkboxes={[
                    {
                      labelProps: {
                        label: 'Easy to access',
                      },
                      checkboxProps: {
                        name: 'isEasyToAccess',
                      },
                    },
                  ]}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </FormModal>
      <DeleteConfirmation
        open={billingWarningOpen}
        title='No billing configured'
        text='No storage billing is configured for this location. Continue?'
        handleClose={() => setBillingWarningOpen(false)}
        handleDelete={() => {
          setBillingWarningOpen(false);
          billingWarningConfirmedRef.current = true;
          formik.handleSubmit();
        }}
        btnText='Continue'
        cancelText='Go back'
        submitting={submitting}
      />
    </Fragment>
  );
}

const useStyles = makeStyles({ name: { WarehouseLocationForm } })((theme) => ({
  layout: {
    padding: '0 16px 16px 16px',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  input: {
    width: '100%',
  },
  subSectionHeader: {
    color: '#000000',
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 24,
    marginTop: 50,
  },
}));

const schema = object().shape(
  {
    position: string().nullable().required('Required'),
    // position: string()
    //   .nullable()
    //   .when('freeTextPosition', (freeTextPosition, schema) => {
    //     return !freeTextPosition ? schema.required('Required') : schema;
    //   }),
    // freeTextPosition: string()
    //   .nullable()
    //   .when('position', (position, schema) => {
    //     return !position ? schema.required('Required') : schema;
    //   }),
    level: string()
      .nullable()
      .when('type', {
        is: 'LongTermStorage',
        then: (schema) => schema.required('Required'),
      }),
    category: string()
      .nullable()
      .when('type', {
        is: 'LongTermStorage' as WarehouseLocationType,
        then: (schema) => schema.required('Required'),
      }),
    capableWeightPounds: string()
      .nullable()
      .when(['type', 'category'], {
        is: (
          type: WarehouseLocationType,
          category: WarehouseStorageCategoryType,
        ) => type === 'LongTermStorage' && category !== 'Floor',
        then: (schema) => schema.required('Required'),
      }),
    capableLengthInches: string()
      .nullable()
      .when(['type', 'category'], {
        is: (
          type: WarehouseLocationType,
          category: WarehouseStorageCategoryType,
        ) => type === 'LongTermStorage' && category !== 'Floor',
        then: (schema) => schema.required('Required'),
      }),
    capableWidthInches: string()
      .nullable()
      .when(['type', 'category'], {
        is: (
          type: WarehouseLocationType,
          category: WarehouseStorageCategoryType,
        ) => type === 'LongTermStorage' && category !== 'Floor',
        then: (schema) => schema.required('Required'),
      }),
    capableHeightInches: string()
      .nullable()
      .when(['type', 'category'], {
        is: (
          type: WarehouseLocationType,
          category: WarehouseStorageCategoryType,
        ) => type === 'LongTermStorage' && category !== 'Floor',
        then: (schema) => schema.required('Required'),
      }),
  },
  // [['freeTextPosition', 'position']],
);
